import { useRef } from 'react';
import axios, { AxiosResponse, CancelToken, CancelTokenSource } from 'axios';
import axiosInstance from 'src/api/axiosInstances/privateAxiosInstance';
import { FetchState, FETCH_ACTION_TYPES } from 'src/reducers/fetchReducer';
import { isAxiosResponse } from 'src/utils/typeGuardsAxios';
import useSafeReducer from './useSafeReducer';

const MAX_RETRY = 3;
const RETRY_DELAY = 1000;

const { loading: SET_LOADING, setData: SET_DATA, setError: SET_ERROR } = FETCH_ACTION_TYPES;

const initialState = {
  loading: false,
  error: false,
  data: null,
} satisfies FetchState<null>;

const asyncDelay = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay));

const getAsyncResponse = async <T>(location: string, step: number, token: CancelToken): Promise<T> => {
  // TODO: check with Dipanshu if API could respond with an error
  const url = `/oc/async?asyncUrl=${location}`;
  const response: T | null = await axiosInstance.get(url, { cancelToken: token });
  if (response) {
    return response;
  }
  if (step < MAX_RETRY) {
    await asyncDelay(RETRY_DELAY);
    return getAsyncResponse(location, step + 1, token);
  }
  throw new Error('Max retry has been reached');
};

const extractData = async <T>(response: AxiosResponse<T>, token: CancelToken): Promise<T> => {
  const location: string | undefined = response.headers.location;
  if (location) {
    return getAsyncResponse(location, 1, token);
  }
  throw new Error('"location" is missing in response');
};

type Fetcher<T> = (token: CancelToken) => Promise<T | AxiosResponse<T>>;

type Options = {
  dispatchCancelledError?: boolean;
};

const useAsyncApiFetch = <T>(
  fetcher: Fetcher<T>,
  options?: Options
): [FetchState<T>, () => Promise<void>, () => void] => {
  const [state, dispatch] = useSafeReducer<T>(initialState);
  const source = useRef<CancelTokenSource | null>(null);

  const refetch = async () => {
    cancelRequest();
    // TODO: we have to update `axios` library and use `AbortController` instead
    // This API is deprecated since `axios@0.22.0` but currently we use `axios@0.19.2`
    source.current = axios.CancelToken.source();
    dispatch({ type: SET_LOADING, payload: { loading: true } });
    try {
      const { token } = source.current;
      const response: T | AxiosResponse<T> = await fetcher(token);
      if (isAxiosResponse(response)) {
        dispatch({ type: SET_DATA, payload: { data: await extractData(response, token) } });
      } else {
        dispatch({ type: SET_DATA, payload: { data: response } });
      }
    } catch (e) {
      if (!axios.isCancel(e) || options?.dispatchCancelledError) {
        dispatch({ type: SET_ERROR, payload: { error: e } });
      }
    }
  };

  const cancelRequest = () => {
    if (source.current) {
      source.current.cancel();
    }
  };

  return [state, refetch, cancelRequest];
};

export default useAsyncApiFetch;
